import { useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useRevit } from '../../contexts/RevitContext';
import './TrackingAlertsSetting.css';

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

const TrackingAlertsSetting = () => {
    const { username, isSubAccountActive, activeSubAccountName } = useRevit();
    const { getToken } = useAuth();
    const [enabled, setEnabled] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!username) return undefined;
        let cancelled = false;

        const load = async () => {
            setError('');
            try {
                const token = await getToken();
                const response = await fetch(`https://${SERVER_URL}/api/tracking-alerts/${encodeURIComponent(username)}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!response.ok) throw new Error('Failed to load alert setting');
                const data = await response.json();
                if (!cancelled) setEnabled(data.enabled === true);
            } catch (err) {
                if (!cancelled) setError('Could not load this setting.');
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [username, getToken]);

    const onToggle = async () => {
        if (enabled === null || saving) return;
        const next = !enabled;
        setEnabled(next);
        setSaving(true);
        setError('');
        try {
            const token = await getToken();
            const response = await fetch(`https://${SERVER_URL}/api/tracking-alerts`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ username, enabled: next }),
            });
            if (!response.ok) throw new Error('Failed to save alert setting');
        } catch (err) {
            setEnabled(!next);
            setError('Could not save this setting.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="tracking-alerts-setting">
            <h3>Notifications</h3>
            {enabled === null && !error ? (
                <p className="tracking-alerts-status">Loading...</p>
            ) : (
                <label className="tracking-alerts-row">
                    <span className="tracking-alerts-copy">
                        <span className="tracking-alerts-title">Tracking stopped emails</span>
                        <span className="tracking-alerts-description">
                            Email me when an offer has no clicks for 5 days. You'll still see the alert in Notifications.
                        </span>
                        {isSubAccountActive && activeSubAccountName && (
                            <span className="tracking-alerts-account">Applies to {activeSubAccountName}.</span>
                        )}
                    </span>
                    <span className="tracking-alerts-switch">
                        <input
                            type="checkbox"
                            className="tracking-alerts-switch-input"
                            checked={enabled === true}
                            disabled={enabled === null || saving}
                            onChange={onToggle}
                            aria-label="Email me when tracking stops"
                        />
                        <span className="tracking-alerts-switch-track">
                            <span className="tracking-alerts-switch-thumb" />
                        </span>
                    </span>
                </label>
            )}
            {error && <p className="tracking-alerts-error">{error}</p>}
        </div>
    );
};

export default TrackingAlertsSetting;
